import type { ConnectorActionState } from "../../shared/types";
import { Icon, type IconName } from "./Icon";
import { Choice } from "./ui";

/** The three things an action can be, in one place so a row and a group's header say them the same way. */
export const PERMISSIONS: { value: ConnectorActionState; label: string; hint: string; icon: IconName }[] = [
  { value: "allow", label: "Always allow", hint: "Agents do it without asking.", icon: "allow" },
  { value: "approval", label: "Needs approval", hint: "A person approves each call first.", icon: "hand" },
  { value: "never", label: "Block", hint: "Refused every time, with no way to propose it.", icon: "block" },
];

/**
 * One action's permission on a row: the three as icons, the open one raised. `options` narrows them where one can't be
 * chosen (an action added by hand has no Always allow: it would be no rule, and no row).
 */
export function PermissionChoice({ label, value, onChange, disabled = false, options = PERMISSIONS }: {
  label: string;
  value: ConnectorActionState;
  onChange: (value: ConnectorActionState) => void;
  disabled?: boolean;
  options?: typeof PERMISSIONS;
}) {
  return (
    <Choice
      compact
      label={label}
      value={value}
      onChange={onChange}
      disabled={disabled}
      options={options.map((p) => ({ value: p.value, title: `${p.label}. ${p.hint}`, label: <Icon name={p.icon} className="size-4" /> }))}
    />
  );
}

/** A group's permission, for everything in it at once: the same words in a dropdown, saying Mixed when its rows differ. */
export function PermissionMenu({ label, value, onChange, disabled = false }: {
  label: string;
  value: ConnectorActionState | "mixed";
  onChange: (value: ConnectorActionState) => void;
  disabled?: boolean;
}) {
  const shown = PERMISSIONS.find((p) => p.value === value);
  return (
    <span className="relative inline-flex shrink-0 items-center">
      {shown && <Icon name={shown.icon} className="pointer-events-none absolute left-2.5 size-4" />}
      <select
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as ConnectorActionState)}
        className={`rounded-full bg-fill py-1 pr-2 text-xs font-medium outline-none transition hover:bg-fill-strong focus:ring-2 focus:ring-haze-deep disabled:opacity-60 dark:focus:ring-haze/60 ${shown ? "pl-8" : "pl-3"}`}
      >
        {value === "mixed" && <option value="mixed" disabled>Mixed</option>}
        {PERMISSIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
    </span>
  );
}
